/**
 * Audit log for client profile changes. Best-effort: never throws; logs on failure.
 */

// Fields we track for Portal (user-editable; no registered_agent_*)
export const PORTAL_AUDIT_FIELDS = [
  'business_name',
  'entity_type',
  'owner_name',
  'owner_email',
  'owner_phone',
  'owner_address_line1',
  'owner_address_line2',
  'owner_city',
  'owner_state',
  'owner_zip',
  'business_address_line1',
  'business_address_line2',
  'business_city',
  'business_state',
  'business_zip',
  'business_phone',
  'business_email',
  'ein',
  'registration_number',
];

// Admin can also change registered_agent_* (append to portal list for admin source)
export const ADMIN_EXTRA_AUDIT_FIELDS = [
  'registered_agent_name',
  'registered_agent_address_line1',
  'registered_agent_address_line2',
  'registered_agent_city',
  'registered_agent_state',
  'registered_agent_zip',
  'registered_agent_phone',
  'registered_agent_email',
];

function normalize(v) {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return s;
}

/**
 * Build list of audit rows: one per changed field.
 * @param {Object} oldObj - current client row (or null for create)
 * @param {Object} newPayload - new values (same keys as fields)
 * @param {number} clientId
 * @param {string|null} userId - auth user id
 * @param {'portal'|'admin'|'approval'|'deletion_snapshot'} source
 * @param {string[]} fields - field names to diff (use PORTAL_AUDIT_FIELDS or PORTAL_AUDIT_FIELDS.concat(ADMIN_EXTRA_AUDIT_FIELDS))
 * @returns {Array<{client_id, user_id, field_name, old_value, new_value, source}>}
 */
export function buildProfileChangeRows(oldObj, newPayload, clientId, userId, source, fields) {
  const rows = [];
  const now = new Date().toISOString();
  for (const field of fields) {
    const oldVal = oldObj && (oldObj[field] !== undefined) ? oldObj[field] : null;
    const newVal = newPayload[field] !== undefined ? newPayload[field] : null;
    if (normalize(oldVal) === normalize(newVal)) continue;
    rows.push({
      client_id: clientId,
      user_id: userId || null,
      changed_at: now,
      field_name: field,
      old_value: oldVal != null ? String(oldVal) : null,
      new_value: newVal != null ? String(newVal) : null,
      source,
    });
  }
  return rows;
}

/**
 * Insert audit rows. Best-effort: on failure logs and does not throw.
 * @param {object} supabase - Supabase client
 * @param {Array<object>} rows - from buildProfileChangeRows
 */
export async function insertProfileChangeAudit(supabase, rows) {
  if (!rows || rows.length === 0) return;
  try {
    const { error } = await supabase.from('client_profile_changes').insert(rows);
    if (error) {
      console.error('[audit] client_profile_changes insert failed:', error.message, error);
    }
  } catch (e) {
    console.error('[audit] client_profile_changes exception:', e);
  }
}

/**
 * One-shot: build diff and insert audit. Never throws.
 * @param {object} supabase - Supabase client
 * @param {Object} oldObj - current client row (or null)
 * @param {Object} newPayload - new values
 * @param {number} clientId
 * @param {string|null} userId
 * @param {'portal'|'admin'|'approval'|'deletion_snapshot'} source
 * @param {boolean} includeAdminFields - if true, include ADMIN_EXTRA_AUDIT_FIELDS
 */
export async function auditProfileChanges(supabase, oldObj, newPayload, clientId, userId, source, includeAdminFields = false) {
  const fields = includeAdminFields
    ? [...PORTAL_AUDIT_FIELDS, ...ADMIN_EXTRA_AUDIT_FIELDS]
    : PORTAL_AUDIT_FIELDS;
  const rows = buildProfileChangeRows(oldObj, newPayload, clientId, userId, source, fields);
  await insertProfileChangeAudit(supabase, rows);
}
